import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Clock } from 'lucide-react';

export const ClockInCard = ({ 
  isClockedIn, 
  clockInTime, 
  todayHours, 
  onClockIn, 
  onClockOut,
  loading = false,
  compact = false,
}) => {
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [currentSessionHours, setCurrentSessionHours] = useState(0);

  // Update elapsed time every second when clocked in
  useEffect(() => {
    let interval;
    if (isClockedIn && clockInTime) {
      const updateElapsed = () => {
        const now = new Date();
        const clockIn = new Date(clockInTime);
        const diff = Math.floor((now - clockIn) / 1000); // difference in seconds
        
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        
        setElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
        setCurrentSessionHours(diff / 3600);
      };
      
      updateElapsed(); // Run immediately
      interval = setInterval(updateElapsed, 1000);
    } else {
      setElapsedTime('00:00:00');
      setCurrentSessionHours(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isClockedIn, clockInTime]);

  return (
    <Card 
      className={`stat-card ${compact ? 'sm:col-span-2 py-0' : ''} ${isClockedIn ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}
      data-testid="clock-status-card"
    >
      <CardHeader className={compact ? 'pb-1 pt-3 px-3' : 'pb-2'}>
        <CardTitle className={`flex items-center gap-2 ${compact ? 'text-sm font-semibold' : 'stat-title'}`}>
          <Clock className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} ${isClockedIn ? 'text-green-600' : 'text-gray-500'}`} />
          Clock Status
        </CardTitle>
      </CardHeader>
      <CardContent className={compact ? 'px-3 pb-3 pt-0' : ''}>
        <div className={`font-bold ${compact ? 'text-base' : 'text-2xl'} ${isClockedIn ? 'text-green-600' : 'text-gray-500'}`}>
          {isClockedIn ? 'Clocked In' : 'Clocked Out'}
        </div>
        
        {isClockedIn && clockInTime && (
          <>
            <div className={`mt-2 ${compact ? 'p-1.5' : 'p-2'} bg-green-100 rounded-lg`}>
              <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-green-700 uppercase tracking-wide`}>Running Time</p>
              <p className={`font-mono font-bold text-green-700 ${compact ? 'text-lg' : 'text-2xl'}`} data-testid="running-time">
                {elapsedTime}
              </p>
            </div>
            <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-gray-500 mt-1.5`}>
              Started at {new Date(clockInTime).toLocaleTimeString()}
            </p>
          </>
        )}
        
        {!isClockedIn && todayHours > 0 && (
          <div className={`mt-2 ${compact ? 'p-1.5' : 'p-2'} bg-blue-50 rounded-lg`}>
            <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-blue-700 uppercase tracking-wide`}>Today's Total</p>
            <p className={`font-bold text-blue-700 ${compact ? 'text-base' : 'text-xl'}`} data-testid="today-hours">
              {todayHours.toFixed(2)} hrs
            </p>
          </div>
        )}
        
        {!isClockedIn && todayHours === 0 && (
          <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500 mt-2`}>
            No hours logged today
          </p>
        )}
        
        <div className={compact ? 'mt-2' : 'mt-3'}>
          {isClockedIn ? (
            <Button 
              onClick={onClockOut} 
              className={`w-full bg-red-600 hover:bg-red-700 ${compact ? 'h-8 text-sm' : ''}`}
              data-testid="clock-out-btn"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Clock Out'}
            </Button>
          ) : (
            <Button 
              onClick={onClockIn} 
              className={`w-full bg-green-600 hover:bg-green-700 ${compact ? 'h-8 text-sm' : ''}`}
              data-testid="clock-in-btn"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Clock In'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ClockInCard;
